// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { Link } from "wouter";
import { MessageCircle, Bot, ArrowRight, Zap, Globe, PhoneCall, Calculator, Users } from "lucide-react";

const PRODUCTS = [
  {
    href: "/ai-chat-widget",
    icon: MessageCircle,
    iconBg: "bg-yellow-50",
    iconColor: "text-yellow-500",
    borderColor: "border-yellow-400",
    badgeBg: "bg-yellow-50 text-yellow-800",
    badge: "Live Now",
    title: "AI Chat Widget",
    tagline: "Turn website visitors into leads — 24/7, on autopilot",
    description:
      "Embed a smart AI chatbot on your website in minutes. It answers questions, captures leads, and syncs with your knowledge base — so you never miss a prospect again.",
    bullets: [
      { icon: Zap, text: "Instant setup — no coding required" },
      { icon: Globe, text: "Works on any website or landing page" },
      { icon: Bot, text: "Trained on your own knowledge base" },
    ],
    ctaLabel: "See Plans & Pricing",
    ctaClass:
      "bg-gradient-to-r from-[#f9cb07] to-[#ffcd3c] hover:from-[#e6b800] hover:to-[#f9cb07] text-black",
  },
  {
    href: "/instant-quote-widget",
    icon: Calculator,
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    borderColor: "border-green-500",
    badgeBg: "bg-green-50 text-green-800",
    badge: "New",
    title: "Instant Quote Widget",
    tagline: "Turn 'what's the price?' into a captured lead — instantly",
    description:
      "Embed an AI-powered quote calculator on your website. Visitors answer 3 questions and get a real price range — then submit their details to receive it. You get the lead, they get the quote.",
    bullets: [
      { icon: Calculator, text: "AI calculates price ranges from your context" },
      { icon: Users, text: "Captures name, email & phone before showing price" },
      { icon: Zap, text: "Works for any trades or service business" },
    ],
    ctaLabel: "See Plans & Pricing",
    ctaClass:
      "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white",
  },
  // HIDDEN: pending Meta app approval — re-enable when approved or switch to Twilio
  // {
  //   href: "/whatsapp-ai",
  //   icon: PhoneCall,
  //   iconBg: "bg-green-50",
  //   iconColor: "text-green-600",
  //   borderColor: "border-green-500",
  //   badgeBg: "bg-green-50 text-green-800",
  //   badge: "New",
  //   title: "WhatsApp AI Bot",
  //   tagline: "Reply to every WhatsApp inquiry instantly — even at 3am",
  //   description:
  //     "Give your business a dedicated Australian WhatsApp number powered by AI. It greets customers, answers FAQs, and alerts you the moment a real conversation starts.",
  //   bullets: [
  //     { icon: MessageCircle, text: "Dedicated AU WhatsApp Business number" },
  //     { icon: Zap, text: "Auto-replies from your knowledge base" },
  //     { icon: PhoneCall, text: "Owner alert on every new conversation" },
  //   ],
  //   ctaLabel: "See Plans & Pricing",
  //   ctaClass:
  //     "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white",
  // },
];

export default function ProductsHighlight() {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block bg-yellow-50 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full mb-3 tracking-wide uppercase">
            AI Products
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Two products. One goal — grow your business on autopilot.
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-lg">
            Pick the tool that fits how your customers enquire, or use both together.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.href}
                className={`relative flex flex-col rounded-2xl border-2 ${p.borderColor} bg-white shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}
              >
                <div className="p-8 flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`${p.iconBg} rounded-xl p-3`}>
                      <Icon className={`w-6 h-6 ${p.iconColor}`} />
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${p.badgeBg}`}>
                      {p.badge}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{p.title}</h3>
                  <p className="text-sm font-semibold text-gray-500 mb-4">{p.tagline}</p>
                  <p className="text-gray-600 mb-6 leading-relaxed">{p.description}</p>

                  <ul className="space-y-2 mb-8">
                    {p.bullets.map(({ icon: BulletIcon, text }) => (
                      <li key={text} className="flex items-center gap-2 text-sm text-gray-700">
                        <BulletIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        {text}
                      </li>
                    ))}
                  </ul>

                  <Link href={p.href}>
                    <button
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 hover:shadow-md ${p.ctaClass}`}
                    >
                      {p.ctaLabel}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
